import { useState, useRef, useEffect } from 'react';
import { useApp } from '../AppContext';
import { supabase } from '../supabaseClient';
import * as XLSX from 'xlsx';
import { inputStyle } from '../utils';
import { FOLDERS } from './DocumentVault';

const CATEGORIES = ['Loan Payment', 'Materials', 'Repairs', 'Insurance', 'Utilities', 'Labor', 'Equipment', 'Platform Fees', 'Other'];

const STORAGE_FREE_LIMIT = 100 * 1024 * 1024; // 100 MB across all vault docs for trial users

function sanitizeVaultFileName(name) {
  return (name || 'untitled').replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 100);
}

function fmtBytes(n) {
  if (!n) return '0 B';
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
  if (n < 1024 * 1024 * 1024) return (n / 1024 / 1024).toFixed(1) + ' MB';
  return (n / 1024 / 1024 / 1024).toFixed(2) + ' GB';
}

export default function ImportTab() {
  const { transactions, setTransactions, properties, business, subscription, recurring, setRecurring, setActiveTab, setFilterProp } = useApp();

  const [importStep, setImportStep] = useState('upload');
  const [importRows, setImportRows] = useState([]);
  const [importParsing, setImportParsing] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importDoneCount, setImportDoneCount] = useState(0);
  const [importPasteText, setImportPasteText] = useState('');
  const [importDragging, setImportDragging] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [importDuplicates, setImportDuplicates] = useState([]);
  // 'import' for CSV/XLSX, 'scan' for image/PDF — controls source label and trial counter
  const [importSource, setImportSource] = useState('import');
  const importDropRef = useRef(null);

  // Primary Smart Upload (vault filing + optional transaction extraction)
  const [smartUploading, setSmartUploading] = useState(false);
  // { file, suggestion: { folder, folder_confidence, property_id, property_confidence, suggested_filename, reasoning } }
  const [pendingSmartUpload, setPendingSmartUpload] = useState(null);
  const [smartSuccess, setSmartSuccess] = useState(null); // { fileName, propertyName, folderLabel }
  const [smartError, setSmartError] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [storageUsage, setStorageUsage] = useState(0); // bytes currently in vault for this business
  // Smart Upload metadata carried into the transaction preview so we can ALSO
  // file the source doc in the vault when the user confirms the import.
  // { file, propertyId, folder, fileName, enabled, docType: 'receipt'|'bank_statement' }
  const [vaultMeta, setVaultMeta] = useState(null);

  // Load current vault storage usage so we can render the trial-cap meter
  // and gate uploads before sending bytes upstream.
  useEffect(() => {
    if (!business?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('documents').select('file_size').eq('business_id', business.id);
      if (cancelled) return;
      const bytes = (data || []).reduce((s, d) => s + (d.file_size || 0), 0);
      setStorageUsage(bytes);
    })();
    return () => { cancelled = true; };
  }, [business?.id]);

  useEffect(() => {
    if (importStep === 'duplicates' && importDuplicates.length === 0) {
      setImportStep('done');
    }
  }, [importStep, importDuplicates]);

  const isPro = subscription?.status === 'pro';
  const existingImported = transactions.filter(t => t.source === 'import').length;
  const FREE_LIMIT = 50;
  const PRO_WARN_LIMIT = 300;
  const SCAN_TRIAL_LIMIT = 10;
  const scanBatchCount = new Set(
    transactions.filter(t => t.source === 'scan' && t.batch_id).map(t => t.batch_id)
  ).size;
  const scanLimitReached = !isPro && scanBatchCount >= SCAN_TRIAL_LIMIT;

  const thisMonthImported = (() => {
    if (!isPro) return 0;
    const start = new Date(); start.setDate(1); start.setHours(0, 0, 0, 0);
    return transactions.filter(t => t.source === 'import' && new Date(t.created_at) >= start).length;
  })();

  const activeImportRows = importRows.filter(r => !r._deleted);
  const wouldExceedFree = !isPro && (existingImported + activeImportRows.length) > FREE_LIMIT;
  const proMonthWarning = isPro && thisMonthImported + activeImportRows.length > PRO_WARN_LIMIT;

  // Crash-recovery: if the previous Smart Upload attempt killed the browser
  // tab mid-resize, the localStorage flag will still be set when the app
  // reboots. Show a helpful message instead of letting the user retry the
  // same too-big file blindly. Runs once on first mount of ImportTab.
  useEffect(() => {
    try {
      const crashed = localStorage.getItem('ll_smart_upload_in_progress');
      if (crashed) {
        localStorage.removeItem('ll_smart_upload_in_progress');
        setSmartError(
          `Your last Smart Upload crashed your browser, most likely because the file was too large for the device to process. ` +
          `File: ${crashed}. ` +
          `Try a smaller photo. On iPhone: Settings → Camera → Formats → 'Most Compatible' produces smaller JPEGs.`
        );
      }
    } catch {/* localStorage can throw in private mode — ignore */}
  }, []);

  function resetImport() {
    setImportStep('upload');
    setImportRows([]);
    setImportError(null);
    setImportDoneCount(0);
    setImportParsing(false);
    setImportPasteText('');
    setImportDragging(false);
    setImportFileName('');
    setImportDuplicates([]);
    setImportSource('import');
    setVaultMeta(null);
  }

  function updateRow(id, field, value) {
    setImportRows(prev => prev.map(r => r._id === id ? { ...r, [field]: value } : r));
  }

  function includeTransfer(id) {
    setImportRows(prev => prev.map(r => r._id === id
      ? { ...r, _deleted: false, type: r.amount >= 0 ? 'income' : 'expense' }
      : r));
  }

  function excludeTransfer(id) {
    setImportRows(prev => prev.map(r => r._id === id ? { ...r, _deleted: true } : r));
  }

  async function runParse(text) {
    setImportParsing(true);
    setImportError(null);
    try {
      const res = await fetch('/api/parse-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      const data = await res.json();
      if (!res.ok || data.error) { setImportError(data.error || 'Parsing failed.'); setImportParsing(false); return; }
      if (data.warning) setImportError(data.warning);

      let rows = data.transactions.map((t, i) => ({
        ...t,
        _id: i,
        _transfer: t.type === 'transfer',
        _deleted: t.type === 'transfer',
        property_id: '',
        isDuplicate: transactions.some(ex =>
          ex.transaction_date === t.transaction_date &&
          Math.abs(Number(ex.amount)) === Math.abs(t.amount) &&
          (ex.description || '').toLowerCase() === (t.description || '').toLowerCase()
        ),
      }));

      const descMap = {};
      [...transactions, ...rows].forEach(t => {
        const key = (t.description || '').toLowerCase().trim();
        if (!descMap[key]) descMap[key] = [];
        descMap[key].push(t);
      });
      rows = rows.map(r => {
        if (r._transfer || r.recurring) return r;
        const key = (r.description || '').toLowerCase().trim();
        const group = descMap[key] || [];
        if (group.length < 2) return r;
        const months = new Set(group.map(t => (t.transaction_date || '').slice(0, 7)).filter(Boolean));
        if (months.size < 2) return r;
        const days = group.map(t => {
          const d = new Date((t.transaction_date || '') + 'T12:00:00');
          return isNaN(d) ? -1 : d.getDate();
        }).filter(d => d >= 1);
        const avgDay = Math.round(days.reduce((a, b) => a + b, 0) / days.length);
        if (days.every(d => Math.abs(d - avgDay) <= 5)) {
          return { ...r, recurring: true, guessedFrequency: 'monthly' };
        }
        return r;
      });

      setImportRows(rows);
      setImportStep('preview');
    } catch (e) {
      setImportError('Network error. Please try again.');
    } finally {
      setImportParsing(false);
    }
  }

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.onerror = () => reject(new Error('Could not read file'));
      reader.readAsDataURL(file);
    });
  }

  // Resize image client-side to keep payload under Claude's vision limits and
  // strip EXIF metadata (including location) for privacy. Designed to avoid
  // mobile-Safari OOM crashes (which kill the whole tab) by:
  //   1. Skipping the canvas pipeline entirely for files already small enough.
  //   2. Using createImageBitmap (off-thread decode) instead of new Image()
  //      when supported — way lighter peak memory on iOS.
  //   3. Lower default quality + smaller max side for tight memory budgets.
  async function resizeImageToBase64(file, maxSide = 1600, quality = 0.78) {
    // Most phone JPEGs land at 2–4 MB. Send those straight through — Claude
    // vision accepts up to ~5 MB and we save the entire decode + canvas +
    // re-encode memory cycle that triggers the OOM on mid-range phones.
    const SMALL_ENOUGH = 4 * 1024 * 1024;
    const passThroughTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (file.size <= SMALL_ENOUGH && passThroughTypes.includes(file.type)) {
      const base64 = await fileToBase64(file);
      return { base64, mediaType: file.type };
    }

    // For larger files: decode with createImageBitmap when available.
    let bitmap = null;
    if (typeof createImageBitmap === 'function') {
      try {
        bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      } catch {
        // Some browsers can't decode certain formats (e.g. HEIC in Chrome) —
        // fall through to the classic <img> path.
      }
    }
    if (!bitmap) {
      bitmap = await new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload  = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not decode image')); };
        img.src = url;
      });
    }

    const srcW = bitmap.width  || bitmap.naturalWidth;
    const srcH = bitmap.height || bitmap.naturalHeight;
    const scale = Math.min(1, maxSide / Math.max(srcW, srcH));
    const w = Math.max(1, Math.round(srcW * scale));
    const h = Math.max(1, Math.round(srcH * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bitmap, 0, 0, w, h);

    // Free the decoded source bitmap memory immediately. On mobile this is
    // the difference between staying under the budget and triggering a tab
    // kill during the toBlob step below.
    if (bitmap.close) bitmap.close();

    return new Promise((resolve, reject) => {
      canvas.toBlob(blob => {
        if (!blob) return reject(new Error('Could not resize image'));
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result;
          const comma = result.indexOf(',');
          // Help GC release the canvas backing store ASAP
          canvas.width = 0; canvas.height = 0;
          resolve({ base64: comma >= 0 ? result.slice(comma + 1) : result, mediaType: 'image/jpeg' });
        };
        reader.onerror = () => reject(new Error('Could not encode resized image'));
        reader.readAsDataURL(blob);
      }, 'image/jpeg', quality);
    });
  }

  async function runScanParse(file) {
    setImportParsing(true);
    setImportError(null);
    try {
      const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
      let payload;
      if (isPdf) {
        const base64 = await fileToBase64(file);
        payload = { image: base64, mediaType: 'application/pdf', fileType: 'pdf' };
      } else {
        const { base64, mediaType } = await resizeImageToBase64(file);
        payload = { image: base64, mediaType, fileType: 'image' };
      }
      const res = await fetch('/api/parse-document', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setImportError(data.error || 'Document parsing failed.');
        setImportParsing(false);
        return;
      }
      if (!data.transactions || data.transactions.length === 0) {
        setImportError(data.warning || "We couldn't find any transactions in this document.");
        setImportParsing(false);
        return;
      }
      const rows = data.transactions.map((t, i) => ({
        ...t,
        _id: i,
        _transfer: false,
        _deleted: false,
        property_id: '',
        isDuplicate: transactions.some(ex =>
          ex.transaction_date === t.transaction_date &&
          Math.abs(Number(ex.amount)) === Math.abs(t.amount) &&
          (ex.description || '').toLowerCase() === (t.description || '').toLowerCase()
        ),
      }));
      if (data.warning) setImportError(data.warning);
      setImportRows(rows);
      setImportStep('preview');
    } catch (e) {
      setImportError('Could not process document: ' + e.message);
    } finally {
      setImportParsing(false);
    }
  }

  // ── Smart Upload (primary path): classifies any file with Claude vision,
  //     then files it into the right property's vault subfolder. If AI is
  //     unsure about property or folder, prompts the user.
  // Use the original file extension if the AI-suggested filename didn't include one
  function ensureFileExtension(fileName, originalFile) {
    if (/\.[a-zA-Z0-9]{1,5}$/.test(fileName)) return fileName;
    const origExt = (originalFile.name || '').match(/\.[a-zA-Z0-9]{1,5}$/);
    return origExt ? fileName + origExt[0] : fileName;
  }

  async function saveToVault(file, propertyId, folderKey, customFileName) {
    const displayName = customFileName ? ensureFileExtension(customFileName, file) : file.name;
    const safe = sanitizeVaultFileName(displayName);
    const path = `${business.id}/${propertyId}/${folderKey}/${crypto.randomUUID()}-${safe}`;
    const { error: upErr } = await supabase.storage
      .from('property-documents')
      .upload(path, file, { contentType: file.type || 'application/octet-stream' });
    if (upErr) throw new Error(upErr.message);
    const { data: doc, error: insErr } = await supabase
      .from('documents')
      .insert([{
        business_id: business.id,
        property_id: propertyId,
        folder_type: folderKey,
        file_name: displayName,
        file_size: file.size,
        storage_path: path,
        mime_type: file.type || null,
      }])
      .select()
      .single();
    if (insErr) {
      // Roll back the storage upload if metadata insert failed
      await supabase.storage.from('property-documents').remove([path]);
      throw new Error(insErr.message);
    }
    setStorageUsage(prev => prev + file.size);
    return doc;
  }

  async function runSmartVaultUpload(file) {
    if (!file) return;
    setSmartError('');
    setSmartSuccess(null);

    if (properties.length === 0) {
      setSmartError('Add a property first before importing documents.');
      return;
    }

    // Hard cap on a single upload — beyond this, browsers (especially iOS
    // Safari / iPhone PWA) routinely OOM during the canvas-resize step.
    const SINGLE_UPLOAD_MAX = 25 * 1024 * 1024; // 25 MB
    if (file.size > SINGLE_UPLOAD_MAX) {
      setSmartError(`This file is ${fmtBytes(file.size)}, which is too large for your browser to process safely. Please use a file under ${fmtBytes(SINGLE_UPLOAD_MAX)}. For multi-page scans, split into smaller PDFs.`);
      return;
    }

    // Trial storage cap — defensive: validate storageUsage is a sane number
    // before comparing. Show real numbers so a wrong trigger is debuggable.
    if (!isPro) {
      const used = Number.isFinite(storageUsage) && storageUsage >= 0 ? storageUsage : 0;
      const willUse = used + file.size;
      if (willUse > STORAGE_FREE_LIMIT) {
        setSmartError(
          `Free trial vault storage limit. You've used ${fmtBytes(used)} of ${fmtBytes(STORAGE_FREE_LIMIT)}; ` +
          `this file is ${fmtBytes(file.size)}, which would put you at ${fmtBytes(willUse)}. ` +
          `Upgrade to Pro for unlimited storage, or try a smaller file.`
        );
        return;
      }
    }

    const isImage = (file.type || '').startsWith('image/');
    const isPdf   = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

    // For file types Claude vision can't read (Word/Excel/etc.), skip the API
    // call and just let the user pick the property + folder manually.
    if (!isImage && !isPdf) {
      setPendingSmartUpload({
        file,
        suggestion: {
          folder: 'property_docs',
          folder_confidence: 'low',
          property_id: properties.length === 1 ? properties[0].id : null,
          property_confidence: properties.length === 1 ? 'high' : 'low',
          reasoning: 'AI classification works on photos and PDFs only. Please pick where this should go.',
        },
      });
      return;
    }

    setSmartUploading(true);
    // Crash beacon: if the browser kills the tab during the decode/resize
    // step, the next page load reads this flag and shows a recovery message
    // instead of leaving the user stranded with a generic OOM banner.
    try { localStorage.setItem('ll_smart_upload_in_progress', `${file.name} (${fmtBytes(file.size)})`); } catch {}
    try {
      let payload;
      if (isPdf) {
        const base64 = await fileToBase64(file);
        payload = { image: base64, mediaType: 'application/pdf' };
      } else {
        const { base64, mediaType } = await resizeImageToBase64(file);
        payload = { image: base64, mediaType };
      }
      // Resize/encode survived → clear the beacon before the network call
      try { localStorage.removeItem('ll_smart_upload_in_progress'); } catch {}

      payload.properties = properties.map(p => ({ id: p.id, name: p.name, address: p.address }));

      const res = await fetch('/api/classify-document', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (!res.ok || data.error) {
        setPendingSmartUpload({
          file,
          suggestion: {
            folder: 'property_docs',
            folder_confidence: 'low',
            property_id: properties.length === 1 ? properties[0].id : null,
            property_confidence: properties.length === 1 ? 'high' : 'low',
            reasoning: 'AI classification failed: ' + (data.error || 'unknown error') + '. Please pick where this should go.',
          },
        });
        return;
      }

      const docType = data.document_type;
      const hasTransactions = Array.isArray(data.transactions) && data.transactions.length > 0;

      // ── Branch A: financial document with transactions to extract ─────────
      // (receipt → file in vault automatically + show preview to confirm tx)
      // (bank_statement → show preview as primary; vault filing is opt-in)
      if ((docType === 'receipt' || docType === 'bank_statement') && hasTransactions) {
        const propertyForRows = data.property_id || (properties.length === 1 ? properties[0].id : '');
        const rows = data.transactions.map((t, i) => ({
          ...t,
          _id: i,
          _transfer: false,
          _deleted: false,
          property_id: propertyForRows,
          isDuplicate: transactions.some(ex =>
            ex.transaction_date === t.transaction_date &&
            Math.abs(Number(ex.amount)) === Math.abs(t.amount) &&
            (ex.description || '').toLowerCase() === (t.description || '').toLowerCase()
          ),
        }));
        setImportRows(rows);
        setImportSource('scan');
        setImportError(null);
        // Carry file + vault prefs into the preview step
        setVaultMeta({
          file,
          docType,
          propertyId: data.property_id || properties[0]?.id || '',
          folder: data.folder || (docType === 'receipt' ? 'receipts' : 'tax'),
          fileName: data.suggested_filename || file.name,
          enabled: docType === 'receipt', // default ON for receipts, OFF for statements
        });
        setImportStep('preview');
        return;
      }

      // ── Branch B: non-financial document — file in vault only ─────────────
      const sureProperty = data.property_confidence === 'high' && data.property_id;
      const sureFolder   = data.folder_confidence === 'high';
      if (sureProperty && sureFolder) {
        const doc = await saveToVault(file, data.property_id, data.folder, data.suggested_filename);
        const prop = properties.find(p => p.id === data.property_id);
        const folder = FOLDERS.find(f => f.key === data.folder);
        setSmartSuccess({
          fileName: doc.file_name,
          propertyName: prop?.name || 'Property',
          folderLabel: folder?.label || data.folder,
        });
      } else {
        setPendingSmartUpload({ file, suggestion: data });
      }
    } catch (e) {
      // The exception fired (browser didn't kill the tab) — clear the beacon
      try { localStorage.removeItem('ll_smart_upload_in_progress'); } catch {}
      console.error('Smart Upload error:', e, 'file:', { name: file.name, size: file.size, type: file.type });
      const msg = (e?.message || '').toLowerCase();
      // iOS Safari / mobile browsers commonly throw OOM during canvas/base64
      // on full-resolution camera shots. Surface a helpful message rather
      // than a cryptic browser-level error string.
      if (msg.includes('memory') || msg.includes('quota') || msg.includes('maximum call stack') || msg.includes('out of')) {
        setSmartError(`Your browser ran out of memory processing this ${fmtBytes(file.size)} file. Try a smaller image or take a new photo at lower resolution (Settings → Camera → Formats on iPhone).`);
      } else {
        setSmartError('Could not file document: ' + (e?.message || 'unknown error'));
      }
    } finally {
      setSmartUploading(false);
    }
  }

  async function confirmSmartUpload(propertyId, folderKey, fileName) {
    const file = pendingSmartUpload?.file;
    if (!file) return;
    setPendingSmartUpload(null);
    setSmartUploading(true);
    setSmartError('');
    try {
      const doc = await saveToVault(file, propertyId, folderKey, fileName);
      const prop = properties.find(p => p.id === propertyId);
      const folder = FOLDERS.find(f => f.key === folderKey);
      setSmartSuccess({
        fileName: doc.file_name,
        propertyName: prop?.name || 'Property',
        folderLabel: folder?.label || folderKey,
      });
    } catch (e) {
      console.error('confirmSmartUpload error:', e);
      setSmartError('Could not file document: ' + (e?.message || 'unknown error'));
    } finally {
      setSmartUploading(false);
    }
  }

  async function handleParseFile(file) {
    setImportError(null);
    setImportFileName(file.name);
    const isImage = (file.type || '').startsWith('image/') || /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(file.name);
    const isPdf   = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

    if (isImage || isPdf) {
      if (scanLimitReached) {
        setImportError(`Free trial limit reached: ${SCAN_TRIAL_LIMIT} of ${SCAN_TRIAL_LIMIT} document scans used. Upgrade for unlimited scans.`);
        return;
      }
      setImportSource('scan');
      setImportStep('upload');
      await runScanParse(file);
      return;
    }

    // CSV / XLSX path
    setImportSource('import');
    setImportParsing(true);
    setImportStep('upload');
    try {
      let text = '';
      if (file.name.match(/\.xlsx?$/i)) {
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        text = XLSX.utils.sheet_to_csv(ws);
      } else {
        text = await file.text();
      }
      await runParse(text);
    } catch (e) {
      setImportError('Could not read file: ' + e.message);
      setImportParsing(false);
    }
  }

  async function handleConfirmImport() {
    if (wouldExceedFree) return;
    const batchId = crypto.randomUUID();
    const toInsert = importRows
      .filter(r => !r._deleted)
      .map(r => ({
        business_id: business.id,
        property_id: r.property_id || null,
        transaction_date: r.transaction_date,
        description: r.description,
        category: r.category,
        amount: (r.type === 'expense' || (r.type === 'transfer' && r.amount < 0)) ? -Math.abs(r.amount) : Math.abs(r.amount),
        type: r.type === 'transfer' ? (r.amount > 0 ? 'income' : 'expense') : r.type,
        source: importSource,
        batch_id: batchId,
      }));
    if (toInsert.length === 0) return;

    const { data: inserted, error } = await supabase.from('transactions').insert(toInsert).select();
    if (error) { setImportError('Import failed: ' + error.message); return; }

    const prevTransactions = transactions;
    setTransactions(prev => [...prev, ...inserted]);

    const activeRows = importRows.filter(r => !r._deleted);
    const recurringRows = activeRows.filter(r => r.recurring);
    if (recurringRows.length > 0) {
      const seen = new Set();
      const recurringTemplates = recurringRows
        .filter(r => {
          const key = (r.description || '').toLowerCase().trim() + '|' + (r.guessedFrequency || 'monthly');
          if (seen.has(key)) return false;
          seen.add(key); return true;
        })
        .map(r => {
          const resolvedType = r.type === 'transfer' ? (r.amount > 0 ? 'income' : 'expense') : r.type;
          const resolvedAmt = resolvedType === 'expense' ? -Math.abs(r.amount) : Math.abs(r.amount);
          const freq = r.guessedFrequency || 'monthly';
          const base = new Date(r.transaction_date + 'T12:00:00');
          const dom = base.getDate();
          const next = new Date(base);
          if (freq === 'monthly') next.setMonth(next.getMonth() + 1);
          else if (freq === 'weekly') next.setDate(next.getDate() + 7);
          else next.setFullYear(next.getFullYear() + 1);
          const today = new Date();
          if (freq === 'monthly') { while (next <= today) next.setMonth(next.getMonth() + 1); }
          else if (freq === 'weekly') { while (next <= today) next.setDate(next.getDate() + 7); }
          else { while (next <= today) next.setFullYear(next.getFullYear() + 1); }
          return {
            business_id: business.id,
            property_id: r.property_id || null,
            description: r.description,
            amount: resolvedAmt,
            type: resolvedType,
            category: r.category || null,
            frequency: freq,
            day_of_month: dom,
            next_due_date: next.toISOString().slice(0, 10),
            active: true,
            batch_id: batchId,
          };
        });

      let { error: rErr } = await supabase.from('recurring_transactions').insert(recurringTemplates);
      if (rErr && rErr.message && rErr.message.includes('batch_id')) {
        const withoutBatch = recurringTemplates.map(({ batch_id, ...rest }) => rest);
        const { error: rErr2 } = await supabase.from('recurring_transactions').insert(withoutBatch);
        if (rErr2) console.error('Recurring insert failed:', rErr2.message);
      } else if (rErr) {
        console.error('Recurring insert failed:', rErr.message);
      }

      const { data: freshRecur } = await supabase.from('recurring_transactions')
        .select('*').eq('business_id', business.id).eq('active', true).order('next_due_date');
      if (freshRecur) setRecurring(freshRecur);
    }

    setImportDoneCount(inserted.length);

    const dupes = [];
    (inserted || []).forEach(newTx => {
      prevTransactions.forEach(ex => {
        const sameDate = ex.transaction_date === newTx.transaction_date;
        const sameDesc = (ex.description || '').toLowerCase().trim() === (newTx.description || '').toLowerCase().trim();
        const sameCat  = (ex.category || '') === (newTx.category || '');
        const amtClose = Math.abs(Math.abs(ex.amount) - Math.abs(newTx.amount)) <= 5;
        const daysDiff = Math.abs(
          new Date(ex.transaction_date + 'T12:00:00') - new Date(newTx.transaction_date + 'T12:00:00')
        ) / (1000 * 60 * 60 * 24);
        const isDupe = (sameDate && amtClose) || (sameDate && sameDesc) || (sameDesc && sameCat && daysDiff <= 3);
        if (isDupe && !dupes.some(d => d.existing.id === ex.id && d.imported.id === newTx.id)) {
          dupes.push({ existing: ex, imported: newTx });
        }
      });
    });

    // If Smart Upload routed us here AND the user kept "Also file in vault"
    // enabled, save the source document to the vault now that the txns are
    // persisted. We do this before the done/duplicates step so any vault error
    // surfaces inline rather than after the user has moved on.
    if (vaultMeta?.enabled && vaultMeta.propertyId && vaultMeta.folder) {
      try {
        await saveToVault(vaultMeta.file, vaultMeta.propertyId, vaultMeta.folder, vaultMeta.fileName);
      } catch (e) {
        setImportError('Transactions imported, but vault filing failed: ' + e.message);
      }
    }
    setVaultMeta(null);

    if (dupes.length > 0) {
      setImportDuplicates(dupes);
      setImportStep('duplicates');
    } else {
      setImportStep('done');
    }
  }

  function fmt(n) {
    return (n < 0 ? '-' : '') + '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  if (importStep === 'duplicates') return (
    <div>
      {importParsing && <AIOverlay />}
      <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: "'Courier New', monospace", letterSpacing: 1, marginBottom: 16 }}>REVIEW POSSIBLE DUPLICATES</div>
      <div style={{ background: '#1c1200', border: '1px solid #d97706', borderRadius: 12, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#d97706', lineHeight: 1.6 }}>
        ⚠️ {importDuplicates.length} possible duplicate{importDuplicates.length !== 1 ? 's' : ''} found — same date + similar amount, same date + same payee, or same payee + category within 3 days of an existing record. Choose what to keep for each one.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
        {importDuplicates.map(({ existing, imported }, idx) => (
          <div key={idx} style={{ background: '#0f1117', border: '1px solid #d97706', borderRadius: 12, padding: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div style={{ background: '#080b12', border: '1px solid #1e2235', borderRadius: 8, padding: 10 }}>
                <div style={{ fontSize: 10, color: '#4b5563', marginBottom: 6, fontFamily: "'Courier New', monospace", letterSpacing: 1 }}>EXISTING</div>
                <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600, marginBottom: 4, wordBreak: 'break-word' }}>{existing.description}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: existing.amount >= 0 ? '#4ade80' : '#f87171' }}>{fmt(existing.amount)}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{existing.transaction_date}</div>
              </div>
              <div style={{ background: '#080b12', border: '1px solid #1e3a5f', borderRadius: 8, padding: 10 }}>
                <div style={{ fontSize: 10, color: '#1d4ed8', marginBottom: 6, fontFamily: "'Courier New', monospace", letterSpacing: 1 }}>JUST IMPORTED</div>
                <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600, marginBottom: 4, wordBreak: 'break-word' }}>{imported.description}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: imported.amount >= 0 ? '#4ade80' : '#f87171' }}>{fmt(imported.amount)}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{imported.transaction_date}</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              <button onClick={() => setImportDuplicates(prev => prev.filter((_, i) => i !== idx))}
                style={{ background: '#1a3a2a', border: '1px solid #14532d', borderRadius: 8, padding: '9px 0', color: '#4ade80', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                Keep Both
              </button>
              <button onClick={async () => {
                await supabase.from('transactions').delete().eq('id', imported.id);
                setTransactions(prev => prev.filter(t => t.id !== imported.id));
                setImportDuplicates(prev => prev.filter((_, i) => i !== idx));
              }} style={{ background: '#1a1535', border: '1px solid #7c3aed', borderRadius: 8, padding: '9px 0', color: '#c4b5fd', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                Delete Import
              </button>
              <button onClick={async () => {
                await supabase.from('transactions').delete().eq('id', existing.id);
                setTransactions(prev => prev.filter(t => t.id !== existing.id));
                setImportDuplicates(prev => prev.filter((_, i) => i !== idx));
              }} style={{ background: '#2d1515', border: '1px solid #7f1d1d', borderRadius: 8, padding: '9px 0', color: '#f87171', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                Delete Original
              </button>
            </div>
          </div>
        ))}
      </div>
      <button onClick={() => { setImportDuplicates([]); setImportStep('done'); }}
        style={{ width: '100%', padding: '14px 0', borderRadius: 12, border: 'none', background: '#1e2235', color: '#94a3b8', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
        Skip All & Continue →
      </button>
    </div>
  );

  if (importStep === 'done') return (
    <div>
      {importParsing && <AIOverlay />}
      <div style={{ textAlign: 'center', padding: '48px 16px' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#4ade80', marginBottom: 8 }}>
          {importDoneCount} transaction{importDoneCount !== 1 ? 's' : ''} imported
        </div>
        <div style={{ fontSize: 14, color: '#94a3b8', marginBottom: 32 }}>They're now in your Transactions tab.</div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button onClick={() => { resetImport(); setFilterProp('all'); setActiveTab('transactions'); }}
            style={{ background: '#1d4ed8', border: 'none', borderRadius: 10, padding: '12px 24px', color: '#fff', cursor: 'pointer', fontSize: 15, fontWeight: 600 }}>
            View Transactions
          </button>
          <button onClick={resetImport}
            style={{ background: '#1e2235', border: '1px solid #2d3555', borderRadius: 10, padding: '12px 24px', color: '#94a3b8', cursor: 'pointer', fontSize: 15 }}>
            Import More
          </button>
        </div>
      </div>
    </div>
  );

  if (importStep === 'preview') return (
    <div>
      {importParsing && <AIOverlay />}
      <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: "'Courier New', monospace", letterSpacing: 1, marginBottom: 16 }}>IMPORT — REVIEW TRANSACTIONS</div>

      {wouldExceedFree && (
        <div style={{ background: '#2d1515', border: '1px solid #f87171', borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#f87171', marginBottom: 6 }}>Import limit reached</div>
          <div style={{ fontSize: 13, color: '#fca5a5' }}>
            This import is too large for your free trial. You've used {existingImported} of {FREE_LIMIT} total imported transactions.
            This file would add {activeImportRows.length} more. Try a smaller file or{' '}
            <span onClick={() => setActiveTab('billing')} style={{ color: '#3b82f6', cursor: 'pointer', textDecoration: 'underline' }}>subscribe for full access</span>.
          </div>
        </div>
      )}

      {proMonthWarning && (
        <div style={{ background: '#1a1a2e', border: '1px solid #a78bfa', borderRadius: 12, padding: 14, marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: '#c4b5fd' }}>
            ⚠️ Large import ({activeImportRows.length} transactions). For best results, consider splitting into smaller date ranges.
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 14, color: '#e2e8f0' }}>
          <span style={{ fontWeight: 700, color: '#4ade80' }}>{activeImportRows.length}</span> transactions parsed
          {!isPro && <span style={{ color: '#94a3b8', fontSize: 12 }}> · {existingImported} of {FREE_LIMIT} free slots used</span>}
        </div>
        <button onClick={resetImport}
          style={{ background: 'transparent', border: '1px solid #2d3555', borderRadius: 8, padding: '7px 14px', color: '#94a3b8', cursor: 'pointer', fontSize: 13 }}>
          ← Start Over
        </button>
      </div>

      {importError && (
        <div style={{ background: '#2d1515', border: '1px solid #f87171', borderRadius: 10, padding: 12, marginBottom: 14, color: '#f87171', fontSize: 13 }}>{importError}</div>
      )}

      {importRows.some(r => r._transfer) && (() => {
        const transferRows = importRows.filter(r => r._transfer);
        const includedCount = transferRows.filter(r => !r._deleted).length;
        return (
          <div style={{ background: '#1c1200', border: '1px solid #d97706', borderRadius: 12, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fbbf24', marginBottom: 6 }}>
              ⚠️ {transferRows.length} Possible Transfer{transferRows.length !== 1 ? 's' : ''} Detected
            </div>
            <div style={{ fontSize: 12, color: '#92400e', lineHeight: 1.6, marginBottom: 14 }}>
              These look like transfers between accounts — not income or expenses. Importing them would inflate your totals. They're excluded by default. Check any that represent real income or spending.
              {includedCount > 0 && <span style={{ color: '#d97706' }}> ({includedCount} included — will be saved as income or expense based on amount.)</span>}
            </div>
            {transferRows.map(row => (
              <div key={row._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderTop: '1px solid #2a1d00' }}>
                <input type="checkbox" checked={!row._deleted}
                  onChange={e => e.target.checked ? includeTransfer(row._id) : excludeTransfer(row._id)}
                  style={{ width: 18, height: 18, flexShrink: 0, cursor: 'pointer', accentColor: '#d97706' }} />
                <div style={{ flex: 1, opacity: row._deleted ? 0.4 : 1 }}>
                  <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600 }}>{row.description}</div>
                  <div style={{ fontSize: 11, color: '#92400e' }}>
                    {row.transaction_date} · <span style={{ color: row.amount >= 0 ? '#4ade80' : '#f87171' }}>{row.amount >= 0 ? '+' : ''}{fmt(row.amount)}</span>
                  </div>
                </div>
                {!row._deleted && (
                  <select value={row.type === 'transfer' ? (row.amount >= 0 ? 'income' : 'expense') : row.type}
                    onChange={e => updateRow(row._id, 'type', e.target.value)}
                    style={{ ...inputStyle, fontSize: 12, padding: '6px 8px', width: 'auto' }}>
                    <option value="income">Income</option>
                    <option value="expense">Expense</option>
                  </select>
                )}
              </div>
            ))}
          </div>
        );
      })()}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
        {importRows.filter(r => !r._deleted && !r._transfer).map(row => (
          <div key={row._id} style={{ background: '#0f1117', border: `1px solid ${row.isDuplicate ? '#854d0e' : '#1e2235'}`, borderRadius: 12, padding: 14 }}>
            {row.isDuplicate && (
              <div style={{ fontSize: 11, color: '#fbbf24', fontFamily: "'Courier New', monospace", marginBottom: 8 }}>⚠ POSSIBLE DUPLICATE</div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, padding: '8px 10px', borderRadius: 8, background: row.recurring ? '#1a1535' : '#0f1117', border: `1px solid ${row.recurring ? '#7c3aed' : '#1e2235'}` }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1 }}>
                <input type="checkbox" checked={!!row.recurring}
                  onChange={e => updateRow(row._id, 'recurring', e.target.checked)}
                  style={{ accentColor: '#a78bfa', cursor: 'pointer', width: 17, height: 17, flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: row.recurring ? 600 : 400, color: row.recurring ? '#c4b5fd' : '#64748b' }}>
                  ↻ Recurring{row.recurring ? ' — will add to schedule' : ''}
                </span>
              </label>
              {row.recurring && (
                <select value={row.guessedFrequency || 'monthly'}
                  onChange={e => updateRow(row._id, 'guessedFrequency', e.target.value)}
                  style={{ ...inputStyle, fontSize: 12, padding: '4px 8px', width: 'auto', flexShrink: 0 }}>
                  <option value="monthly">Monthly</option>
                  <option value="weekly">Weekly</option>
                  <option value="yearly">Yearly</option>
                </select>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
              <input type="date" value={row.transaction_date}
                onChange={e => updateRow(row._id, 'transaction_date', e.target.value)}
                style={{ ...inputStyle, fontSize: 13, padding: '8px 10px' }} />
              <select value={row.type} onChange={e => updateRow(row._id, 'type', e.target.value)}
                style={{ ...inputStyle, fontSize: 13, padding: '8px 10px' }}>
                <option value="income">Income</option>
                <option value="expense">Expense</option>
              </select>
            </div>
            <input value={row.description} onChange={e => updateRow(row._id, 'description', e.target.value)}
              placeholder="Description" style={{ ...inputStyle, fontSize: 13, padding: '8px 10px', marginBottom: 8 }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
              <input type="number" value={Math.abs(row.amount)}
                onChange={e => updateRow(row._id, 'amount', Number(e.target.value))}
                placeholder="Amount" style={{ ...inputStyle, fontSize: 13, padding: '8px 10px' }} />
              <select value={row.category} onChange={e => updateRow(row._id, 'category', e.target.value)}
                style={{ ...inputStyle, fontSize: 13, padding: '8px 10px' }}>
                <option value="Income / Rent">Income / Rent</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center' }}>
              <select value={row.property_id} onChange={e => updateRow(row._id, 'property_id', e.target.value)}
                style={{ ...inputStyle, fontSize: 13, padding: '8px 10px' }}>
                <option value="">Shared / All Properties</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <button onClick={() => updateRow(row._id, '_deleted', true)}
                style={{ background: 'transparent', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 18, padding: '0 6px' }} title="Remove row">✕</button>
            </div>
          </div>
        ))}
      </div>

      {/* Vault-filing widget — only shown when Smart Upload routed us here.
          Receipt default: ON. Bank statement default: OFF (user opts in). */}
      {vaultMeta && (
        <div style={{ background: '#0a1530', border: '1px solid #1e3a5f', borderRadius: 10, padding: 12, marginBottom: 14 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, color: vaultMeta.enabled ? '#93c5fd' : '#94a3b8', fontWeight: 600 }}>
            <input type="checkbox" checked={vaultMeta.enabled}
              onChange={e => setVaultMeta(v => ({ ...v, enabled: e.target.checked }))}
              style={{ accentColor: '#3b82f6', cursor: 'pointer', width: 17, height: 17 }} />
            📁 Also file this {vaultMeta.docType === 'receipt' ? 'receipt' : 'statement'} in the property vault
          </label>
          {vaultMeta.enabled && (
            <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
              <input value={vaultMeta.fileName}
                onChange={e => setVaultMeta(v => ({ ...v, fileName: e.target.value }))}
                placeholder="Filename (AI-suggested, editable)"
                style={{ ...inputStyle, fontSize: 13, padding: '8px 10px' }} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <select value={vaultMeta.propertyId} onChange={e => setVaultMeta(v => ({ ...v, propertyId: e.target.value }))}
                  style={{ ...inputStyle, fontSize: 13, padding: '8px 10px' }}>
                  {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <select value={vaultMeta.folder} onChange={e => setVaultMeta(v => ({ ...v, folder: e.target.value }))}
                  style={{ ...inputStyle, fontSize: 13, padding: '8px 10px' }}>
                  {FOLDERS.map(f => <option key={f.key} value={f.key}>{f.icon} {f.label}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      <button onClick={handleConfirmImport} disabled={wouldExceedFree || activeImportRows.length === 0}
        style={{ width: '100%', padding: '14px 0', borderRadius: 12, border: 'none',
          background: wouldExceedFree || activeImportRows.length === 0 ? '#1e2235' : '#1d4ed8',
          color: wouldExceedFree || activeImportRows.length === 0 ? '#4b5563' : '#fff',
          fontSize: 16, fontWeight: 700, cursor: wouldExceedFree || activeImportRows.length === 0 ? 'not-allowed' : 'pointer' }}>
        {vaultMeta?.enabled
          ? `Import ${activeImportRows.length} Transaction${activeImportRows.length !== 1 ? 's' : ''} + File in Vault`
          : `Import ${activeImportRows.length} Transaction${activeImportRows.length !== 1 ? 's' : ''}`}
      </button>
    </div>
  );

  const storageOverLimit = !isPro && storageUsage >= STORAGE_FREE_LIMIT;
  const smartDisabled = smartUploading || importParsing || properties.length === 0 || storageOverLimit;

  return (
    <div>
      {importParsing && <AIOverlay />}

      <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: "'Courier New', monospace", letterSpacing: 1, marginBottom: 6 }}>📥 IMPORT</div>
      <div style={{ fontSize: 14, color: '#cbd5e1', marginBottom: 16, lineHeight: 1.5 }}>
        Upload any document — receipt, lease, inspection, insurance, anything. AI figures out which property and folder, then files it in your vault.
      </div>

      {/* Storage usage banner (vault filing trial gate) */}
      {!isPro && (
        <div style={{ background: '#0f1117', border: '1px solid #1e2235', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#94a3b8' }}>
          Free trial vault storage:{' '}
          <span style={{ color: storageOverLimit ? '#f87171' : '#4ade80', fontWeight: 600 }}>{fmtBytes(storageUsage)}</span>
          {' '}/ {fmtBytes(STORAGE_FREE_LIMIT)}
          {storageOverLimit && (
            <> · <span onClick={() => setActiveTab('billing')} style={{ color: '#3b82f6', cursor: 'pointer' }}>Upgrade for unlimited →</span></>
          )}
        </div>
      )}

      {properties.length === 0 && (
        <div style={{ background: '#2d1515', border: '1px solid #7f1d1d', borderRadius: 10, padding: '10px 14px', marginBottom: 14, color: '#f87171', fontSize: 13 }}>
          Add a property first before importing documents. <span onClick={() => setActiveTab('properties')} style={{ color: '#fca5a5', cursor: 'pointer', textDecoration: 'underline' }}>Add property →</span>
        </div>
      )}

      {/* Success card after a silent auto-file */}
      {smartSuccess && (
        <div style={{ background: '#0d1a12', border: '1px solid #14532d', borderRadius: 10, padding: '12px 14px', marginBottom: 14, color: '#4ade80', fontSize: 13, lineHeight: 1.5 }}>
          ✅ Filed <strong style={{ color: '#86efac' }}>{smartSuccess.fileName}</strong> in{' '}
          <strong style={{ color: '#86efac' }}>{smartSuccess.propertyName} → {smartSuccess.folderLabel}</strong>.{' '}
          <span onClick={() => setActiveTab('properties')} style={{ color: '#3b82f6', cursor: 'pointer' }}>View vault →</span>{' '}
          <span onClick={() => setSmartSuccess(null)} style={{ color: '#475569', cursor: 'pointer', marginLeft: 6 }}>Dismiss</span>
        </div>
      )}

      {smartError && (
        <div style={{ background: '#2d1515', border: '1px solid #7f1d1d', borderRadius: 10, padding: '10px 14px', marginBottom: 14, color: '#f87171', fontSize: 13 }}>
          {smartError}
        </div>
      )}

      {/* AI classification prompt — appears when AI is unsure about property or folder */}
      {pendingSmartUpload && (
        <SmartUploadPrompt
          pending={pendingSmartUpload}
          properties={properties}
          onConfirm={confirmSmartUpload}
          onCancel={() => setPendingSmartUpload(null)}
        />
      )}

      {/* PRIMARY: Smart Upload + Take Photo */}
      {/* Explicit file-extension whitelist. Avoid wildcards (* / *) and
          avoid image/* — both can cause Android's chooser to route through
          the media picker and offer Camera / Camcorder. Listing only file
          extensions for common document + image types steers the chooser
          straight into the Files / Drive picker on Android, no media
          options shown. */}
      <input id="smartUploadInput" type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.odt,.ods,.jpg,.jpeg,.png,.webp,.heic,.heif,.gif,.tiff,.bmp"
        style={{ display: 'none' }}
        onChange={e => { const f = e.target.files[0]; if (f) runSmartVaultUpload(f); e.target.value = ''; }} />
      <button onClick={() => document.getElementById('smartUploadInput').click()}
        disabled={smartDisabled}
        style={{ width: '100%', padding: '16px 0', borderRadius: 12, border: 'none',
          background: smartDisabled ? '#1e2235' : '#1d4ed8',
          color: smartDisabled ? '#475569' : '#fff',
          fontSize: 16, fontWeight: 700, cursor: smartDisabled ? 'not-allowed' : 'pointer',
          marginBottom: 10 }}>
        {smartUploading ? '🤖 Classifying with AI…' : '📤 Upload File'}
      </button>

      {/* File-extension whitelist (no MIME types, no image/*) — Android
          chooser honors file-extension hints more consistently than MIME
          types and won't offer camcorder/record-video. capture="environment"
          still opens the rear camera directly on iOS and most Android
          browsers. */}
      <input id="smartCameraInput" type="file"
        accept=".jpg,.jpeg,.png,.webp,.heic,.heif"
        capture="environment" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files[0]; if (f) runSmartVaultUpload(f); e.target.value = ''; }} />
      <button onClick={() => document.getElementById('smartCameraInput').click()}
        disabled={smartDisabled}
        style={{ width: '100%', padding: '14px 0', borderRadius: 12, border: '1px solid #2d3555',
          background: smartDisabled ? '#1e2235' : '#0f1117',
          color: smartDisabled ? '#475569' : '#e2e8f0',
          fontSize: 15, fontWeight: 600, cursor: smartDisabled ? 'not-allowed' : 'pointer',
          marginBottom: 18 }}>
        📸 Take Photo
      </button>

      <div style={{ background: '#0f1117', border: '1px solid #1e2235', borderRadius: 10, padding: '12px 14px', fontSize: 12, color: '#64748b', lineHeight: 1.6, marginBottom: 20 }}>
        <div style={{ fontWeight: 600, color: '#94a3b8', marginBottom: 4 }}>How Smart Upload works</div>
        Your file is sent to Claude AI, which identifies the document type and matches it against your properties.
        Files are stored privately in your vault; the AI is instructed to never extract or store personal info
        (names, addresses, phone, card numbers). Images are resized client-side and EXIF metadata is stripped.
        {!isPro && <> Free trial accounts get {fmtBytes(STORAGE_FREE_LIMIT)} of vault storage.</>}
      </div>

      {/* ADVANCED: Bank statements & spreadsheets (transaction extraction) */}
      <button onClick={() => setShowAdvanced(v => !v)}
        style={{ width: '100%', background: 'transparent', border: '1px solid #1e2235', borderRadius: 10, padding: '10px 14px', color: '#94a3b8', cursor: 'pointer', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span>⚙️ Advanced: Extract transactions from CSV, Excel, or bank statements</span>
        <span style={{ fontSize: 11 }}>{showAdvanced ? '▲' : '▼'}</span>
      </button>

      {showAdvanced && (
        <div style={{ background: '#0a0d16', border: '1px solid #1e2235', borderRadius: 12, padding: 14, marginBottom: 20 }}>
          {!isPro && (
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12, lineHeight: 1.6 }}>
              Free trial: <span style={{ color: existingImported >= FREE_LIMIT ? '#f87171' : '#4ade80', fontWeight: 600 }}>{existingImported}</span>/{FREE_LIMIT} imported rows ·{' '}
              <span style={{ color: scanBatchCount >= SCAN_TRIAL_LIMIT ? '#f87171' : '#4ade80', fontWeight: 600 }}>{scanBatchCount}</span>/{SCAN_TRIAL_LIMIT} receipt scans
            </div>
          )}

          <div
            ref={importDropRef}
            onDragOver={e => { e.preventDefault(); setImportDragging(true); }}
            onDragLeave={() => setImportDragging(false)}
            onDrop={e => { e.preventDefault(); setImportDragging(false); const f = e.dataTransfer.files[0]; if (f) handleParseFile(f); }}
            onClick={() => document.getElementById('importFileInput').click()}
            style={{ border: `2px dashed ${importDragging ? '#3b82f6' : '#2d3555'}`, borderRadius: 12, padding: '20px 14px', textAlign: 'center', cursor: 'pointer', marginBottom: 12, background: importDragging ? '#0d1b3e' : '#0f1117', transition: 'border-color 0.15s, background 0.15s' }}>
            <div style={{ fontSize: 24, marginBottom: 6 }}>📂</div>
            <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600, marginBottom: 4 }}>Drop a CSV, Excel, or receipt file</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>AI extracts transactions and adds them to your books</div>
            <input id="importFileInput" type="file" accept=".csv,.xlsx,.xls,image/*,application/pdf" style={{ display: 'none' }}
              onChange={e => { const f = e.target.files[0]; if (f) handleParseFile(f); e.target.value = ''; }} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div style={{ flex: 1, height: 1, background: '#1e2235' }} />
            <div style={{ fontSize: 11, color: '#475569', fontFamily: "'Courier New', monospace" }}>OR PASTE TEXT</div>
            <div style={{ flex: 1, height: 1, background: '#1e2235' }} />
          </div>

          <textarea value={importPasteText} onChange={e => setImportPasteText(e.target.value)}
            placeholder="Paste CSV data, bank export, or tab/comma separated text…"
            rows={5} style={{ ...inputStyle, resize: 'vertical', fontSize: 13, fontFamily: "'Courier New', monospace", marginBottom: 10 }} />

          {importError && (
            <div style={{ background: '#2d1515', border: '1px solid #f87171', borderRadius: 10, padding: 10, marginBottom: 10, color: '#f87171', fontSize: 13 }}>{importError}</div>
          )}

          <button
            onClick={() => { if (importPasteText.trim()) runParse(importPasteText); }}
            disabled={importParsing || !importPasteText.trim()}
            style={{ width: '100%', padding: '12px 0', borderRadius: 10, border: 'none',
              background: importParsing || !importPasteText.trim() ? '#1e2235' : '#1d4ed8',
              color: importParsing || !importPasteText.trim() ? '#4b5563' : '#fff',
              fontSize: 14, fontWeight: 600, cursor: importParsing || !importPasteText.trim() ? 'not-allowed' : 'pointer' }}>
            {importParsing ? 'Parsing with AI…' : 'Parse with AI →'}
          </button>
        </div>
      )}
    </div>
  );
}

function SmartUploadPrompt({ pending, properties, onConfirm, onCancel }) {
  const s = pending.suggestion;
  const aiPropOk = s.property_id && properties.some(p => p.id === s.property_id);
  const [selectedPropertyId, setSelectedPropertyId] = useState(aiPropOk ? s.property_id : (properties[0]?.id || ''));
  const [selectedFolder, setSelectedFolder] = useState(s.folder || 'property_docs');
  const [fileName, setFileName] = useState(s.suggested_filename || pending.file.name.replace(/\.[^.]+$/, ''));

  const suggestedProp = properties.find(p => p.id === s.property_id);
  const fc = s.folder_confidence;
  const pc = s.property_confidence;
  const cConf = c => c === 'high' ? '#4ade80' : c === 'medium' ? '#fbbf24' : '#94a3b8';

  return (
    <div style={{ background: '#0a0d16', border: '1px solid #3b82f6', borderRadius: 12, padding: 16, marginBottom: 14 }}>
      <div style={{ fontSize: 11, color: '#3b82f6', fontFamily: "'Courier New', monospace", letterSpacing: 1, marginBottom: 10 }}>
        🤖 CONFIRM FILING
      </div>
      <div style={{ fontSize: 13, color: '#e2e8f0', marginBottom: 10, wordBreak: 'break-all' }}>
        <span style={{ color: '#94a3b8' }}>File:</span> {pending.file.name}
      </div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12, fontStyle: 'italic', lineHeight: 1.5 }}>
        "{s.reasoning}"
      </div>

      <label style={{ display: 'block', fontSize: 11, color: '#94a3b8', fontFamily: "'Courier New', monospace", marginBottom: 4 }}>
        FILENAME
      </label>
      <input value={fileName} onChange={e => setFileName(e.target.value)}
        placeholder="e.g. Home Depot Receipt - May 2026"
        style={{ ...inputStyle, fontSize: 14, padding: '10px 12px', marginBottom: 12 }} />

      <label style={{ display: 'block', fontSize: 11, color: '#94a3b8', fontFamily: "'Courier New', monospace", marginBottom: 4 }}>
        PROPERTY <span style={{ color: cConf(pc), marginLeft: 6 }}>· AI {pc}</span>
        {suggestedProp && <span style={{ color: '#475569', marginLeft: 6 }}>(suggested: {suggestedProp.name})</span>}
      </label>
      <select value={selectedPropertyId} onChange={e => setSelectedPropertyId(e.target.value)}
        style={{ ...inputStyle, fontSize: 14, padding: '10px 12px', marginBottom: 12 }}>
        {properties.map(p => (
          <option key={p.id} value={p.id}>{p.name}{p.address ? ` — ${p.address}` : ''}</option>
        ))}
      </select>

      <label style={{ display: 'block', fontSize: 11, color: '#94a3b8', fontFamily: "'Courier New', monospace", marginBottom: 4 }}>
        FOLDER <span style={{ color: cConf(fc), marginLeft: 6 }}>· AI {fc}</span>
      </label>
      <select value={selectedFolder} onChange={e => setSelectedFolder(e.target.value)}
        style={{ ...inputStyle, fontSize: 14, padding: '10px 12px', marginBottom: 14 }}>
        {FOLDERS.map(f => (
          <option key={f.key} value={f.key}>{f.icon} {f.label}</option>
        ))}
      </select>

      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => onConfirm(selectedPropertyId, selectedFolder, fileName.trim() || null)}
          disabled={!selectedPropertyId}
          style={{ flex: 1, background: selectedPropertyId ? '#1d4ed8' : '#1e2235', border: 'none', borderRadius: 10, padding: '10px 0', color: '#fff', cursor: selectedPropertyId ? 'pointer' : 'not-allowed', fontSize: 14, fontWeight: 600 }}>
          File it
        </button>
        <button onClick={onCancel}
          style={{ background: 'transparent', border: '1px solid #2d3555', borderRadius: 10, padding: '10px 18px', color: '#94a3b8', cursor: 'pointer', fontSize: 14 }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function AIOverlay() {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(8,11,18,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'all' }}>
      <div style={{ background: '#0f1117', border: '1px solid #2d3555', borderRadius: 18, padding: '36px 32px', maxWidth: 340, width: '90%', textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, border: '4px solid #1e2235', borderTop: '4px solid #3b82f6', borderRadius: '50%', animation: 'spin 0.9s linear infinite', margin: '0 auto 20px' }} />
        <div style={{ fontSize: 17, fontWeight: 700, color: '#e2e8f0', marginBottom: 10 }}>Processing your file with AI…</div>
        <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>This may take 30–60 seconds.<br />Please do not close this page or upload another file.</div>
      </div>
    </div>
  );
}
